import {
  ArtistVerify,
  ArtistVerifySearchParams,
  ArtistVerifyStatus,
  getArtistVerifyList,
} from '@/api/artistVerify';
import SearchForm, { SearchFormItem } from '@/components/SearchForm';
import TabsTable, { TabsTableRef } from '@/components/TabsTable';
import { FORMAT_TIME } from '@/types/common';
import {
  Button,
  DatePicker,
  Form,
  Image,
  Input,
  TableColumnType,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const statusColorMap = {
  [ArtistVerifyStatus.APPROVED]: 'green',
  [ArtistVerifyStatus.REJECTED]: 'red',
  [ArtistVerifyStatus.PENDING]: 'orange',
};

export const statusTextMap = {
  [ArtistVerifyStatus.APPROVED]: '认证通过',
  [ArtistVerifyStatus.REJECTED]: '认证驳回',
  [ArtistVerifyStatus.PENDING]: '待认证',
};

const ArtistVerifyPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<{
    id: string;
    name: string;
    email: string;
    suppId: string;
    submitTime: string;
    actionUser: string;
  }>();

  const tabsTableRef = useRef<TabsTableRef>(null);
  const [ids, setIds] = useState<string[]>([]);

  const SearchFormItems: SearchFormItem[] = [
    {
      name: 'id',
      label: '认证 ID',
      component: <Input size="small" />,
    },
    {
      name: 'name',
      label: '用户姓名',
      component: <Input size="small" />,
    },
    {
      name: 'email',
      label: '用户 Email',
      component: <Input size="small" />,
    },
    {
      name: 'suppId',
      label: 'SUPP ID',
      component: <Input size="small" />,
    },
    {
      name: 'submitTime',
      label: '认证提交日期',
      component: (
        <DatePicker
          size="small"
          style={{ width: '100%' }}
          placeholder=""
          format="YYYY-MM-DD"
        />
      ),
    },
    {
      name: 'actionUser',
      label: '操作人',
      component: <Input size="small" />,
    },
  ];

  const handleSearch = () => {
    tabsTableRef.current?.refresh();
  };

  const tabItems = [
    {
      key: 'all',
      label: '汇总',
    },
    {
      key: ArtistVerifyStatus.APPROVED.toString(),
      label: '认证通过',
    },
    {
      key: ArtistVerifyStatus.REJECTED.toString(),
      label: '认证驳回',
    },
    {
      key: ArtistVerifyStatus.PENDING.toString(),
      label: '待认证',
    },
  ];

  const handleActionClick = (index: number) => {
    const searchParams = new URLSearchParams();
    searchParams.set('index', index.toString());
    searchParams.set('ids', ids.join(','));
    navigate(`/artist-verify/verify-detail?${searchParams.toString()}`);
  };

  const columns: TableColumnType<ArtistVerify>[] = [
    {
      title: '认证 ID',
      dataIndex: 'certificationId',
      key: 'certificationId',
    },
    {
      title: 'SUPP ID',
      dataIndex: 'suppId',
      key: 'suppId',
    },
    {
      title: '证明图片',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (_, record) => {
        return <Image src={record.imageUrl} width={100} />;
      },
    },
    {
      title: '真实姓名',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: '审核状态',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        return (
          <Tag color={statusColorMap[record.status]}>
            {statusTextMap[record.status]}
          </Tag>
        );
      },
    },
    {
      key: 'createTime',
      dataIndex: 'createTime',
      title: '认证提交时间',
      render: (item: string) => (
        <span>
          {dayjs(item).isValid() ? dayjs(item).format(FORMAT_TIME) : '-'}
        </span>
      ),
    },
    {
      title: '审核时间',
      dataIndex: 'processTime',
      key: 'processTime',
    },
    {
      title: '操作人',
      dataIndex: 'operatorName',
      key: 'operatorName',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, __, index) => {
        return (
          <Button type="link" onClick={() => handleActionClick(index)}>
            审核操作
          </Button>
        );
      },
    },
  ];

  const queryFn = async (page: number, pageSize: number, tabKey?: string) => {
    const values = form.getFieldsValue();
    const params: ArtistVerifySearchParams = {
      pageSize,
      currentPage: page,
      status: tabKey !== 'all' ? Number(tabKey) : undefined,
      certificationId: values.id ? values.id : undefined,
      userName: values.name ? values.name : undefined,
      userEmail: values.email ? values.email : undefined,
      suppId: values.suppId ? values.suppId : undefined,
      createDateStart: values.submitTime
        ? dayjs(values.submitTime).format('YYYY-MM-DD')
        : undefined,
      createDateEnd: values.submitTime
        ? dayjs(values.submitTime).add(1, 'day').format('YYYY-MM-DD')
        : undefined,
      operatorName: values.actionUser ? values.actionUser : undefined,
    };
    const resp = await getArtistVerifyList(params);
    if (resp.status !== 200) {
      return {
        total: 0,
        records: [],
        currentPage: page,
        pageSize,
      };
    }
    const ids = resp.data.records.map((item) => item.certificationId);
    setIds(ids);
    return resp.data;
  };

  return (
    <div>
      <SearchForm items={SearchFormItems} form={form} onFinish={handleSearch} />
      <div style={{ marginTop: 10 }}>
        <TabsTable
          ref={tabsTableRef}
          defaultTabKey="all"
          tabItems={tabItems}
          rowKey="certificationId"
          columns={columns}
          queryKey="artistVerify"
          queryFn={queryFn}
          pageSize={20}
        />
      </div>
    </div>
  );
};

export default ArtistVerifyPage;
